import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  CircularProgress,
  Snackbar,
  Alert,
  Tooltip,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Visibility as ViewIcon,
  Save as SaveIcon,
  Close as CloseIcon,
  FilterAltOff as FilterAltOffIcon,
  Update as UpdateIcon,
  Description as DocIcon
} from '@mui/icons-material';
import { getAdminUser } from '../../utils/authSessionStorage';
import { useLanguage } from '../../context/LanguageContext';
import { legalAPI } from '../../services/api';

/**
 * LegalPages Component
 * Handles Privacy Policy and Terms & Conditions uploads.
 * Integrated with multipart upload API.
 */
const LegalPages = () => {
  const { t, locale } = useLanguage();
  const adminUser = getAdminUser();
  const isSuperAdmin = /super\s*admin/i.test(adminUser?.role || '');

  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState(null);

  // Filter States
  const [categoryFilter, setCategoryFilter] = useState('Privacy Policy');
  const [platformFilter, setPlatformFilter] = useState('app');


  const [formData, setFormData] = useState({
    category: 'Privacy Policy',
    platform: 'app',
    version: '1.0.0',
    changes: '',
    is_active: true,
    content: '',
    file: null // Actual File object for multipart upload
  });

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Fetch History from API
  const fetchHistory = useCallback(async () => {
    setLoading(true);
    try {
      let allDocs = [];

      // Determine which platforms to fetch
      const platformsToFetch = platformFilter === 'All' ? ['app', 'customer'] : [platformFilter];

      // Determine which categories to fetch
      const categoriesToFetch = categoryFilter === 'All' ? ['Privacy Policy', 'Terms & Conditions'] : [categoryFilter];

      const fetchPromises = [];

      platformsToFetch.forEach(p => {
        if (categoriesToFetch.includes('Privacy Policy')) {
          fetchPromises.push(
            legalAPI.getPrivacyHistory(p).then(res => {
              const data = Array.isArray(res.data) ? res.data : (res.data?.payload || []);
              return data.map(doc => ({
                ...doc,
                category: 'Privacy Policy',
                platform: doc.platform || p,
                changes: doc.changeOfContent || doc.changes,
                is_active: doc.isActive !== undefined ? doc.isActive : doc.is_active,
                effective_at: doc.effectiveAt || doc.effective_at,
                created_at: doc.createdAt || doc.created_at
              }));
            }).catch(() => [])
          );
        }
        if (categoriesToFetch.includes('Terms & Conditions')) {
          fetchPromises.push(
            legalAPI.getTermsHistory(p).then(res => {
              const data = Array.isArray(res.data) ? res.data : (res.data?.payload || []);
              return data.map(doc => ({
                ...doc,
                category: 'Terms & Conditions',
                platform: doc.platform || p,
                changes: doc.changeOfContent || doc.changes,
                is_active: doc.isActive !== undefined ? doc.isActive : doc.is_active,
                effective_at: doc.effectiveAt || doc.effective_at,
                created_at: doc.createdAt || doc.created_at
              }));
            }).catch(() => [])
          );
        }

      });


      const results = await Promise.all(fetchPromises);
      allDocs = results.flat();

      console.log('--- LEGAL HISTORY DATA ---');
      console.log('Filter Category:', categoryFilter);
      console.log('Filter Platform:', platformFilter);
      console.log('Documents Received:', allDocs);

      // Sort by creation date descending

      allDocs.sort((a, b) => new Date(b.created_at || b.effective_at) - new Date(a.created_at || a.effective_at));

      setDocuments(allDocs);
    } catch (error) {
      console.error('Error fetching history:', error);
      setSnackbar({ open: true, message: t('legal.errLoad'), severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [categoryFilter, platformFilter, t]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);


  const handleOpenDialog = () => {
    setFormData({
      category: 'Privacy Policy',
      platform: 'app',
      version: '1.0.0',
      changes: '',
      is_active: true,
      content: '',
      file: null
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.file && !formData.content) {
      setSnackbar({ open: true, message: t('legal.errNoContent'), severity: 'error' });
      return;
    }

    setSaving(true);
    try {
      const data = new FormData();

      // If we have content but no file, create a virtual file
      let uploadFile = formData.file;
      if (!uploadFile && formData.content) {
        const blob = new Blob([formData.content], { type: 'text/html' });
        uploadFile = new File([blob], `${formData.category.toLowerCase().replace(/\s/g, '_')}.html`);
      }

      data.append('File', uploadFile);
      data.append('Platform', formData.platform);
      data.append('Version', formData.version);
      data.append('ChangeOfContent', formData.changes);

      console.log('--- UPLOADING DOCUMENT ---');
      console.log('Category:', formData.category);
      for (let pair of data.entries()) {
        console.log(pair[0] + ': ' + pair[1]);
      }

      if (formData.category === 'Privacy Policy') {
        await legalAPI.uploadPrivacyPolicy(data);
      } else {
        await legalAPI.uploadTermsAndConditions(data);
      }

      setSnackbar({ 
        open: true, 
        message: t('legal.successUpload').replace('{category}', formData.category === 'Privacy Policy' ? t('legal.privacyPolicy') : t('legal.termsConditions')), 
        severity: 'success' 
      });
      setDialogOpen(false);
      fetchHistory(); // Refresh history automatically

    } catch (error) {
      console.error('Error uploading document:', error);
      setSnackbar({ open: true, message: error?.response?.data?.errorMessage || t('legal.errUpload'), severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const filteredDocuments = useMemo(() => {
    return documents.filter(doc => {
      const matchCategory = categoryFilter === 'All' ||
        doc.category.toLowerCase() === categoryFilter.toLowerCase();

      const matchPlatform = platformFilter === 'All' ||
        (doc.platform && doc.platform.toLowerCase() === platformFilter.toLowerCase());

      return matchCategory && matchPlatform;
    });
  }, [documents, categoryFilter, platformFilter]);


  const generateHtml = (data) => {
    return `<!DOCTYPE html>
<html lang="${locale === 'de' ? 'de' : 'en'}">
<head>
    <meta charset="UTF-8">
    <title>${data.platform} Legal Document - ${data.version}</title>
    <style>
        body { font-family: sans-serif; line-height: 1.6; margin: 40px auto; padding: 20px; }
        .content { width: 100%; }
    </style>
</head>
<body>
    <div class="content">${data.content || t('legal.previewPlaceholder')}</div>
</body>
</html>`;
  };

  const getCategoryLabel = (cat) => {
    if (cat === 'Privacy Policy') return t('legal.privacyPolicy');
    if (cat === 'Terms & Conditions') return t('legal.termsConditions');
    return cat;
  };

  const getPlatformLabel = (plat) => {
    if (plat?.toLowerCase() === 'app') return t('legal.app');
    if (plat?.toLowerCase() === 'customer') return t('legal.customer');
    return plat;
  };

  if (!isSuperAdmin) {
    return (
      <Box p={4} textAlign="center">
        <Typography variant="h5" color="error">{t('legal.accessDenied')}</Typography>
      </Box>
    );
  }

  return (
    <Box p={3}>
      {/* Header Section */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#0F3D3E' }}>
            {t('legal.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('legal.subtitle')}
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenDialog}
          sx={{
            background: 'linear-gradient(135deg, #1b5a5c 0%, #0F3D3E 100%)',
            boxShadow: '0 4px 12px rgba(124, 58, 237, 0.25)',
            borderRadius: '12px',
            px: 4,
            py: 1.2,
            textTransform: 'none',
            fontSize: '1rem',
            fontWeight: 600,
            '&:hover': { boxShadow: '0 6px 16px rgba(124, 58, 237, 0.35)' }
          }}
        >
          {t('legal.addBtn')}
        </Button>
      </Box>

      {/* Filter Bar */}
      <Box sx={{ mb: 3, p: 2, bgcolor: '#fff', borderRadius: '6px', boxShadow: '0 2px 4px rgba(124, 58, 237, 0.05)' }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>{t('legal.filterCategory')}</InputLabel>
              <Select
                value={categoryFilter}
                label={t('legal.filterCategory')}
                onChange={(e) => setCategoryFilter(e.target.value)}
              >
                <MenuItem value="Privacy Policy">{t('legal.privacyPolicy')}</MenuItem>
                <MenuItem value="Terms & Conditions">{t('legal.termsConditions')}</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>{t('legal.filterPlatform')}</InputLabel>
              <Select
                value={platformFilter}
                label={t('legal.filterPlatform')}
                onChange={(e) => setPlatformFilter(e.target.value)}
              >
                <MenuItem value="app">{t('legal.app')}</MenuItem>
                <MenuItem value="customer">{t('legal.customer')}</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          {(categoryFilter !== 'Privacy Policy' || platformFilter !== 'app') && (
            <Grid item xs={12} sm="auto">
              <Button
                variant="outlined"
                size="small"
                startIcon={<FilterAltOffIcon />}
                onClick={() => { setCategoryFilter('Privacy Policy'); setPlatformFilter('app'); }}

                sx={{ 
                  borderRadius: '10px', 
                  color: '#0F3D3E', 
                  borderColor: '#0F3D3E',
                  textTransform: 'none',
                  px: 2
                }}
              >
                {t('legal.clear')}
              </Button>


            </Grid>
          )}
        </Grid>
      </Box>

      {/* Table Section */}
      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          borderRadius: 3,
          border: '1px solid #e0e0e0',
          boxShadow: '0 2px 4px rgba(124, 58, 237, 0.05)',
          '& .MuiTableCell-head': {
            backgroundColor: '#ebf4f4',
            color: '#0F3D3E',
            fontWeight: 700,
            padding: '16px'
          },
          '& .MuiTableCell-body': {
            padding: '16px'
          }
        }}
      >
        <Table>
          <TableHead sx={{ bgcolor: '#ebf4f4' }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 700, color: '#0F3D3E', width: '10%' }}>{t('legal.colVersion')}</TableCell>
              <TableCell sx={{ fontWeight: 700, color: '#0F3D3E', width: '35%' }}>{t('legal.colChanges')}</TableCell>
              <TableCell sx={{ fontWeight: 700, color: '#0F3D3E', width: '15%' }}>{t('legal.colEffectiveDate')}</TableCell>
              <TableCell sx={{ fontWeight: 700, color: '#0F3D3E', width: '15%' }}>{t('legal.colPlatform')}</TableCell>
              <TableCell sx={{ fontWeight: 700, color: '#0F3D3E', width: '15%' }}>{t('legal.colStatus')}</TableCell>
              <TableCell sx={{ fontWeight: 700, color: '#0F3D3E', width: '10%' }} align="right">{t('legal.colDocuments')}</TableCell>
            </TableRow>
          </TableHead>



          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                  <CircularProgress size={30} sx={{ color: '#0F3D3E' }} />
                </TableCell>
              </TableRow>
            ) : filteredDocuments.map((doc) => (
              <TableRow key={doc.id} hover>
                <TableCell sx={{ fontWeight: 600 }}>{doc.version}</TableCell>
                <TableCell sx={{ maxWidth: 200 }}>
                  <Tooltip title={doc.changes || ''} arrow placement="top">
                    <Typography 
                      sx={{ 
                        whiteSpace: 'nowrap', 
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis',
                        fontSize: '0.875rem'
                      }}
                    >
                      {doc.changes}
                    </Typography>
                  </Tooltip>
                </TableCell>

                <TableCell>{doc.effective_at ? doc.effective_at.split('T')[0] : '-'}</TableCell>
                <TableCell>
                  <Chip
                    label={getPlatformLabel(doc.platform).toUpperCase()}
                    size="small"
                    sx={{
                      bgcolor: doc.platform.toLowerCase() === 'app' ? '#0b2e2f' : '#065f46',
                      color: '#fff',
                      fontWeight: 700,
                      fontSize: '0.65rem',
                      borderRadius: '6px',
                      height: '22px'
                    }}
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    label={doc.is_active ? t('legal.active') : t('legal.inactive')}
                    size="small"
                    color={doc.is_active ? 'success' : 'default'}
                  />
                </TableCell>
                <TableCell align="right">
                  <Tooltip title={t('legal.view')}>
                    <IconButton
                      onClick={async () => {
                        setLoading(true);
                        try {
                          const normalizedUrl = doc.file.startsWith('/api/')
                            ? doc.file.substring(4)
                            : doc.file;
                          const response = await legalAPI.getFileContent(normalizedUrl);
                          const content = typeof response.data === 'string' ? response.data : JSON.stringify(response.data);
                          setSelectedDoc({ ...doc, content });
                          setPreviewOpen(true);
                        } catch (error) {
                          setSnackbar({ open: true, message: t('legal.errFetchContent'), severity: 'error' });
                        } finally {
                          setLoading(false);
                        }
                      }}
                      sx={{ color: '#0F3D3E' }}
                    >
                      <DocIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>



              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add Dialog */}
      <Dialog open={dialogOpen} onClose={() => !saving && setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, bgcolor: '#ebf4f4', color: '#0F3D3E' }}>
          {t('legal.dialogTitle')}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>{t('legal.category')}</InputLabel>
                <Select
                  value={formData.category}
                  label={t('legal.category')}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                >
                  <MenuItem value="Privacy Policy">{t('legal.privacyPolicy')}</MenuItem>
                  <MenuItem value="Terms & Conditions">{t('legal.termsConditions')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>{t('legal.platformTarget')}</InputLabel>
                <Select
                  value={formData.platform}
                  label={t('legal.platformTarget')}
                  onChange={(e) => setFormData({ ...formData, platform: e.target.value })}
                >
                  <MenuItem value="app">{t('legal.app')}</MenuItem>
                  <MenuItem value="customer">{t('legal.customer')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t('legal.versionLabel')}
                value={formData.version}
                onChange={(e) => setFormData({ ...formData, version: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Button
                variant="outlined"
                component="label"
                fullWidth
                startIcon={<AddIcon />}
                sx={{ py: 1.5, borderRadius: 2, borderStyle: 'dashed', borderColor: '#0F3D3E', color: '#0F3D3E' }}
              >
                {formData.file ? `${t('legal.fileNamePrefix')}${formData.file.name}` : t('legal.selectFile')}
                <input
                  type="file"
                  hidden
                  accept=".html,.md,.txt"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                      setFormData({ ...formData, file: file });
                      const reader = new FileReader();
                      reader.onload = (event) => {
                        setFormData(prev => ({ ...prev, content: event.target.result }));
                      };
                      reader.readAsText(file);
                    }
                  }}
                />
              </Button>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t('legal.changeSummary')}
                multiline
                rows={2}
                value={formData.changes}
                onChange={(e) => setFormData({ ...formData, changes: e.target.value })}
              />
            </Grid>


          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2, bgcolor: '#ebf4f4' }}>
          <Button onClick={() => setDialogOpen(false)} color="inherit" disabled={saving}>{t('common.cancel')}</Button>
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
            onClick={handleSave}
            disabled={saving}
            sx={{ background: 'linear-gradient(135deg, #1b5a5c 0%, #0a2829 100%)', px: 3 }}
          >
            {saving ? t('legal.uploading') : t('legal.uploadSave')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: '#ebf4f4', color: '#0F3D3E' }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {t('legal.previewTitle')
              .replace('{category}', getCategoryLabel(selectedDoc?.category))
              .replace('{platform}', getPlatformLabel(selectedDoc?.platform))}
          </Typography>
          <IconButton onClick={() => setPreviewOpen(false)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          <iframe
            title="preview-content"
            srcDoc={selectedDoc ? generateHtml(selectedDoc) : ''}
            style={{ width: '100%', height: '70vh', border: 'none', background: '#fff' }}
          />
        </DialogContent>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >

        <Alert severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default LegalPages;
